import React from 'react';
import { Box, Flex, DeprecatedIcon, Text } from '@fullstory/components';
import { FormattedMessage } from 'react-intl';
import { intl } from '@fullstory/intl';

type ComponentType = 'Segment' | 'chart' | 'card';

interface ErrorStateProps {
  component: ComponentType;
  message: string | null | undefined;
  inverse?: boolean;
}

const errorStateChartMessages: { [key in ComponentType]: { errorMessage: string } } = {
  Segment: {
    errorMessage: intl.formatMessage({
      defaultMessage: 'There was an issue fetching data for your Segment.',
      id: 'gnzO4F',
    }),
  },
  chart: {
    errorMessage: intl.formatMessage({
      defaultMessage: 'There was an issue fetching data for your chart.',
      id: 'kkzzGd',
    }),
  },
  card: {
    errorMessage: intl.formatMessage({
      defaultMessage: 'There was an issue fetching data for your card.',
      id: 'P7VNYZ',
    }),
  },
};

const defaultMessage = intl.formatMessage({
  defaultMessage: 'If the problem persists, please try refreshing the page.',
  id: 'Q/DvDe',
});

// ErrorStateChart shows a default error state for metrics/dashboards/funnels/conversions/segments with an option
// for a custom error message
export const ErrorStateChart: React.FC<ErrorStateProps> = ({
  component,
  message,
  inverse = false,
}) => {
  const iconColor = inverse ? 'neutrals-n0' : 'red-dark';
  const textColor = inverse ? 'default-inverse' : 'default';

  return (
    <Box data-component="ErrorStateChart" testId="error-state-chart" padding={[20, 0, 20, 0]}>
      <Flex column>
        <DeprecatedIcon name="error-warning-fill" color={iconColor} size={32} />
        <Box padding={[32, 0, 16, 0]}>
          <Text appearance="heading3" color={textColor}>
            <FormattedMessage defaultMessage="Error" id="KN7zKn" />
          </Text>
        </Box>
        <Box maxWidth={600}>
          <Text appearance="bodyMedium" color={textColor}>
            <>
              {errorStateChartMessages[component].errorMessage}
              <br />
              {message ?? defaultMessage}
            </>
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

export const ErrorStateCard: React.FC<Omit<ErrorStateProps, 'component'>> = ({
  message,
  inverse = false,
}) => {
  const component = 'card';
  const iconColor = inverse ? 'neutrals-n0' : 'red-dark';
  const headingColor = inverse ? 'default-inverse' : 'default';
  const bodyColor = inverse ? 'default-inverse' : 'secondary';

  return (
    <Box data-component="ErrorStateCard" testId="error-state-chart" padding={[20, 0, 20, 0]}>
      <Flex column>
        <DeprecatedIcon name="error-warning-fill" color={iconColor} size={32} />
        <Box padding={[8, 0, 0, 0]}>
          <Text appearance="heading3" color={headingColor}>
            <FormattedMessage defaultMessage="Error" id="KN7zKn" />
          </Text>
        </Box>
        <Box maxWidth={230}>
          <Text appearance="bodySmall" color={bodyColor} style={{ textAlign: 'center' }}>
            {errorStateChartMessages[component].errorMessage} {message ?? defaultMessage}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
